import {
  Component, OnInit, OnDestroy, OnChanges, SimpleChanges,
  Input, Output, EventEmitter,
  signal, computed, ElementRef, HostListener
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TagsService } from '../../services/tags.service';

@Component({
  selector: 'app-tag-input',
  standalone: false,
  templateUrl: './tag-input.component.html',
  styleUrls: ['./tag-input.component.scss']
})
export class TagInputComponent implements OnInit, OnChanges, OnDestroy {
  @Input() module = '';
  @Input() value = '';
  @Output() valueChange = new EventEmitter<string>();

  inputText    = signal('');
  allTags      = signal<string[]>([]);
  showInput    = signal(false);
  isCreating   = signal(false);
  errorMsg     = signal<string | null>(null);

  private valueSignal = signal('');

  selectedTags = computed<string[]>(() =>
    this.valueSignal()
      ? this.valueSignal().split(',').map(t => t.trim()).filter(Boolean)
      : []
  );

  filteredTags = computed(() => {
    const q        = this.inputText().toLowerCase().trim();
    const selected = this.selectedTags();
    const available = this.allTags().filter(t => !selected.includes(t));
    if (!q) return available;
    return available.filter(t => t.toLowerCase().includes(q));
  });

  private destroy$ = new Subject<void>();

  constructor(private tagsService: TagsService, private el: ElementRef) {}

  ngOnInit(): void {
    this.valueSignal.set(this.value);
    if (this.module) {
      this.tagsService.getTags(this.module)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => this.allTags.set(res.data || []),
          error: () => {}
        });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      this.valueSignal.set(changes['value'].currentValue ?? '');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:click', ['$event.target'])
  onDocumentClick(target: Element): void {
    if (!this.el.nativeElement.contains(target)) {
      this.showInput.set(false);
      this.inputText.set('');
      this.errorMsg.set(null);
    }
  }

  openInput(): void {
    this.showInput.set(true);
    this.inputText.set('');
    this.errorMsg.set(null);
  }

  onInputChange(value: string): void {
    this.inputText.set(value);
    this.errorMsg.set(null);
  }

  selectTag(tag: string): void {
    this.addValue(tag);
    this.inputText.set('');
    this.showInput.set(false);
  }

  removeTag(tag: string): void {
    const updated = this.selectedTags().filter(t => t !== tag);
    this.valueChange.emit(updated.join(','));
  }

  onAdd(): void {
    const label = this.inputText().trim();
    if (!label) return;

    const existing = this.allTags().find(t => t.toLowerCase() === label.toLowerCase());
    if (existing) {
      this.selectTag(existing);
      return;
    }

    this.isCreating.set(true);
    this.errorMsg.set(null);
    this.tagsService.createTag(this.module, label)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.allTags.update(tags => [...tags, label].sort((a, b) => a.localeCompare(b)));
          this.addValue(label);
          this.inputText.set('');
          this.showInput.set(false);
          this.isCreating.set(false);
        },
        error: () => {
          this.errorMsg.set('Erreur lors de la création du tag');
          this.isCreating.set(false);
        }
      });
  }

  hasExactMatch(): boolean {
    const q = this.inputText().trim().toLowerCase();
    return !!q && this.allTags().some(t => t.toLowerCase() === q);
  }

  private addValue(tag: string): void {
    const current = this.selectedTags();
    if (current.includes(tag)) return;
    this.valueChange.emit([...current, tag].join(','));
  }
}
